/**
 * InstanceTable — the CCA portfolio's instances: every row added or uploaded,
 * with the server's per-cell remarks shown under the cell.
 *
 * Port of legacy InstancesListTable.vue, CCA branch (ccaHeadersBase :339-418,
 * OCI columns :595-627, one header row :66-86). Legacy draws CCA and EIA with
 * the same component, so this is the shared InstanceTableCore (remarks,
 * problem-first sort, Delete / Delete Error Records, double-click editing, the
 * regions link) given CCA's columns and edit rules — not a copy:
 *  - UUID / Instance Name, Cloud, Region, Size, OS Type, Quantity, Total Number
 *    of Hours per Month, Pricing Model, plus OCPU and Memory on OCI;
 *  - one header row (no "Maximum Bandwidth Used" band);
 *  - Region, Size, OS Type, Quantity, Hours, Pricing Model (and OCPU / Memory
 *    on an OCI flex type) edit; UUID and Cloud do not;
 *  - emptying Quantity, Hours, OCPU or Memory puts the old value back without
 *    asking the server (legacy handleQuantityChange / handlehourschange
 *    :1205-1221, handleInputBlur :993-1003);
 *  - the hint over the errors reads "… Obtain Cost advice" (BulkDelete.vue:24).
 *
 * Same props as components/eia/InstanceTable: rows, provider, loading,
 * onDelete, onDeleteErrors, actionsDisabled, onEditCell, onEditStart,
 * onEditingChange, regionOptions, instanceTypeOptions, emptyMessage,
 * regionsHref. In Billing mode pass the billing cloud (legacy datadogCSP) as
 * `provider`, so "List of … Regions" names it, as legacy does.
 */
import { useMemo } from 'react';

import { InstanceTableCore } from '@components/eia/InstanceTable/InstanceTableCore';

import { CCA_FIELD } from '../ccaFormRules';
import { EDITABLE_FIELDS, REVERT_WHEN_EMPTIED, ccaColumnsFor } from '../ccaInstanceRows';

/** The last step of the "Fix the errors → Save portfolio → Obtain …" hint. */
export const CCA_ADVICE_LABEL = 'Cost advice';

/**
 * Cells the table puts back when emptied: Quantity / Hours (handleQuantityChange
 * / handlehourschange :1205-1221) and OCI OCPU / Memory (handleInputBlur
 * :993-1003 — an emptied cell takes its old value back).
 */
const TABLE_REVERT_WHEN_EMPTIED = Object.freeze([
  ...REVERT_WHEN_EMPTIED,
  CCA_FIELD.OCPU,
  CCA_FIELD.MEMORY,
]);

/**
 * CCA's columns in the shared table's shape: legacy titles and order, values
 * left-aligned as legacy draws them; the UUID column gets the wider cell.
 */
export function ccaTableColumns(provider) {
  return ccaColumnsFor(provider).map((c) => ({
    field: c.key,
    header: c.title,
    editable: c.editable,
    wide: c.key === CCA_FIELD.UUID,
  }));
}

export function InstanceTable({ provider, ...props }) {
  const columns = useMemo(() => ccaTableColumns(provider), [provider]);
  return (
    <InstanceTableCore
      {...props}
      provider={provider}
      columns={columns}
      editableFields={EDITABLE_FIELDS}
      revertWhenEmptied={TABLE_REVERT_WHEN_EMPTIED}
      adviceLabel={CCA_ADVICE_LABEL}
    />
  );
}

export default InstanceTable;
